import { Heading, VStack, Card } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ContactFormattedAddress,
  CustomerPortalBillGroup,
  CustomerPortalContactUpdateRequest,
  CustomerPortalContactUpdateRequestSchema,
  CustomerPortalData,
  CustomerPortalSessionData,
} from '@monetize/types/app';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdateCustomerPortalContact } from '../../../../../api/customerPortalService';
import {
  MBox,
  MButton,
  MCard,
  MFlex,
  MText,
} from '../../../../../components/Monetize';
import { getAddress } from '../../../../../utils/address';
import { ContactsCardBodyEdit } from './ContactsCardBodyEdit';
import { ContactsCardBodyUseShippingAsBilling } from './ContactsCardBodyUseShippingAsBilling';

interface FieldRowProps {
  label: string;
  value: string | React.ReactNode;
}

const formatAddress = (address: ContactFormattedAddress) => {
  if (!address?.fullAddress) {
    return null;
  }
  return (
    <>
      {address.addressLine1 && <MText>{address.addressLine1}</MText>}
      {address.addressLine2 && <MText>{address.addressLine2}</MText>}
      {address.cityStatePCCountry && (
        <MText>{address.cityStatePCCountry}</MText>
      )}
    </>
  );
};

interface ContactsCardProps {
  addressSource: CustomerPortalBillGroup['addressSource'];
  billingContact: CustomerPortalBillGroup['billingContact'];
  shippingContact: CustomerPortalBillGroup['shippingContact'];
  billingAddress: CustomerPortalBillGroup['billingAddress'];
  shippingAddress: CustomerPortalBillGroup['shippingAddress'];
  sessionData: CustomerPortalSessionData;
  permissions: CustomerPortalData['permissions']['contacts'];
}

export const ContactsCard = ({
  addressSource, // TODO: depending on address source, the updates may flow to the account instead of the contact
  billingAddress,
  billingContact,
  shippingAddress,
  shippingContact,
  sessionData,
  permissions,
}: ContactsCardProps) => {
  const [isEditMode, setIsEditMode] = useState(false);

  const { mutateAsync, isPending } = useUpdateCustomerPortalContact({
    meta: { showErrorToast: true },
  });

  const formState = useForm<CustomerPortalContactUpdateRequest>({
    resolver: zodResolver(CustomerPortalContactUpdateRequestSchema),
    mode: 'onChange',
    defaultValues: {
      billingContact: {
        ...billingContact,
        address: billingAddress,
      },
      shippingContact: {
        ...shippingContact,
        address: shippingAddress,
      },
      useSameShippingBilling: billingContact.id === shippingContact.id,
    },
  });
  const { handleSubmit, reset } = formState;

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelClick = () => {
    setIsEditMode(false);
    reset();
  };

  const handleSave = async (data: CustomerPortalContactUpdateRequest) => {
    try {
      if (data.billingContact.email.trim() !== billingContact.email.trim()) {
        // assume new contact if email address changed
        data.billingContact.id = null;
      }
      if (data.useSameShippingBilling) {
        data.shippingContact = data.billingContact;
      } else if (
        // If contact should be different, ensure we never re-use the billing contact if they were the same to begin with
        data.shippingContact.id === data.billingContact.id ||
        // if the email changes, create a new contact
        data.shippingContact.email.trim() !== shippingContact.email.trim()
      ) {
        data.shippingContact.id = null;
      }

      const response = await mutateAsync({ sessionData, payload: data });
      setIsEditMode(false);
      reset({
        billingContact: {
          ...response.billingContact,
          address: response.billingAddress,
        },
        shippingContact: {
          ...response.shippingContact,
          address: response.shippingAddress,
        },
        useSameShippingBilling:
          response.billingContact.id === response.shippingContact.id,
      });
    } catch (ex) {
      //
    }
  };

  return (
    <MBox asChild>
      <form onSubmit={handleSubmit(handleSave)} noValidate>
        <MCard variant="border" borderRadius="md">
          <Card.Header pt={4} pb={2} px={4}>
            <MFlex justifyContent="space-between" alignItems="center">
              <Heading fontWeight="bold" fontSize="lg" color="tPurple.dark">
                Contacts
              </Heading>
              {!isEditMode && permissions.edit && (
                <MButton
                  type="button"
                  variant="tertiary"
                  size="sm"
                  onClick={handleEditClick}
                >
                  Edit
                </MButton>
              )}
              {isEditMode && (
                <MBox>
                  <MButton
                    type="button"
                    size="sm"
                    variant="cancel"
                    mr={2}
                    isDisabled={isPending}
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </MButton>
                  <MButton
                    type="submit"
                    size="sm"
                    variant="primary"
                    isLoading={isPending}
                    isDisabled={isPending}
                  >
                    Save
                  </MButton>
                </MBox>
              )}
            </MFlex>
          </Card.Header>
          <Card.Body pt={0} px={4} pb={4}>
            {!isEditMode && (
              <MFlex wrap="wrap" gap={8}>
                <MBox minW="45%">
                  <Heading
                    fontWeight="bold"
                    fontSize="sm"
                    color="tPurple.dark"
                    mb="2"
                  >
                    Bill To Contact
                  </Heading>
                  <ContactsCardBodyView
                    address={billingAddress}
                    contact={billingContact}
                  />
                </MBox>
                <MBox minW="45%">
                  <Heading
                    fontWeight="bold"
                    fontSize="sm"
                    color="tPurple.dark"
                    mb="2"
                  >
                    Ship To Contact
                  </Heading>
                  <ContactsCardBodyView
                    address={shippingAddress}
                    contact={shippingContact}
                  />
                </MBox>
              </MFlex>
            )}

            {isEditMode && (
              <MFlex
                flexDirection={{ base: 'column', md: 'row' }}
                alignItems="flex-start"
                gap={8}
              >
                <MBox flex={1} minW={0} w="100%">
                  <Heading
                    fontWeight="bold"
                    fontSize="md"
                    color="tPurple.dark"
                    mb="2"
                  >
                    Bill To Contact
                  </Heading>
                  <ContactsCardBodyEdit
                    formKeyPrefix="billingContact"
                    formState={formState}
                    isDisabled={isPending}
                  />
                </MBox>
                <MBox flex={1} minW={0} w="100%">
                  <Heading
                    fontWeight="bold"
                    fontSize="md"
                    color="tPurple.dark"
                    mb="2"
                  >
                    Ship To Contact
                  </Heading>
                  <ContactsCardBodyUseShippingAsBilling
                    formState={formState}
                    isDisabled={isPending}
                  >
                    <ContactsCardBodyEdit
                      formKeyPrefix="shippingContact"
                      formState={formState}
                      isDisabled={isPending}
                    />
                  </ContactsCardBodyUseShippingAsBilling>
                </MBox>
              </MFlex>
            )}
          </Card.Body>
        </MCard>
      </form>
    </MBox>
  );
};

const ContactsCardBodyView = ({
  contact,
  address,
}: {
  contact: CustomerPortalBillGroup['billingContact'];
  address: CustomerPortalBillGroup['billingAddress'];
}) => {
  const formattedAddress = formatAddress(getAddress(address));

  return (
    <VStack gap={4} align="stretch">
      <FieldRow label="Name" value={contact.fullName || 'Not specified'} />
      <FieldRow label="Email" value={contact.email || 'Not specified'} />
      {contact.phone && <FieldRow label="Phone" value={contact.phone} />}
      {formattedAddress && (
        <FieldRow label="Address" value={formattedAddress} />
      )}
    </VStack>
  );
};

const FieldRow = ({ label, value }: FieldRowProps) => (
  <MFlex gap={3} alignItems="flex-start">
    <MBox flex={1}>
      <MText fontSize="xs" color="tGray.darkPurple" fontWeight="500">
        {label}
      </MText>
      {typeof value === 'string' ? (
        <MText fontSize="sm" color="tPurple.dark" whiteSpace="pre-line">
          {value}
        </MText>
      ) : (
        value
      )}
    </MBox>
  </MFlex>
);
